import { useEffect, useState } from "react";
import { AlertTriangle, PackageX } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/hooks/auth";
import { cn } from "@/lib/utils";
import { StockLevelBar } from "@/components/StockLevelBar";
import { acknowledgeStockAlert, getPendingStockAlerts, StockAlert } from "@/Api/stockAlerts";

const POLLING_INTERVAL_MS = 60000;

const formatDate = (value?: string | null) => {
  if (!value) return "-";
  const [y, m, d] = value.slice(0, 10).split("-");
  return `${d}/${m}/${y}`;
};

/**
 * Alerta de estoque (LOT-26) para quem tem a permissão "receive-stock-alerts".
 *
 * Mesmo comportamento do aviso de devolução: interrompe a tela e só fecha em "Ciente".
 * 70% de estoque disponível = aviso (laranja); 50% = alerta (vermelho).
 */
export function StockAlertModal() {
  const auth = useAuth();
  const navigate = useNavigate();
  const isImpersonating = !!auth?.impersonation;

  const [alerts, setAlerts] = useState<StockAlert[]>([]);
  const [acknowledging, setAcknowledging] = useState(false);

  const fetchAlerts = async () => {
    try {
      const { data, ok } = await getPendingStockAlerts();
      if (ok) setAlerts(data?.response ?? []);
    } catch (error) {
      console.error("Erro ao buscar alertas de estoque:", error);
    }
  };

  useEffect(() => {
    if (isImpersonating) return;

    const refetchWhenVisible = () => {
      if (document.visibilityState === "visible") fetchAlerts();
    };

    fetchAlerts();
    const interval = setInterval(fetchAlerts, POLLING_INTERVAL_MS);
    document.addEventListener("visibilitychange", refetchWhenVisible);
    window.addEventListener("focus", refetchWhenVisible);

    return () => {
      clearInterval(interval);
      document.removeEventListener("visibilitychange", refetchWhenVisible);
      window.removeEventListener("focus", refetchWhenVisible);
    };
  }, [isImpersonating]);

  const current = alerts[0];

  const acknowledge = async (thenOpenStock = false) => {
    if (!current) return;
    setAcknowledging(true);
    try {
      const { ok } = await acknowledgeStockAlert(current.uuid);
      if (!ok) return;
      setAlerts((prev) => prev.filter((a) => a.uuid !== current.uuid));
      if (thenOpenStock) navigate("/estoque");
    } catch (error) {
      console.error("Erro ao confirmar alerta de estoque:", error);
      toast.error("Não foi possível confirmar. Tente novamente.");
    } finally {
      setAcknowledging(false);
    }
  };

  if (isImpersonating || !current) return null;

  const danger = current.level === "danger";
  const Icon = danger ? PackageX : AlertTriangle;
  const percent = current.available_percent;
  const percentLabel = `${Number.isInteger(percent) ? percent : percent.toFixed(1).replace(".", ",")}%`;
  const subject =
    current.scope === "contest"
      ? "do estoque final do concurso"
      : `do estoque do produto ${current.product_name ?? ""}`.trim();

  return (
    <AlertDialog open>
      <AlertDialogContent className="max-w-lg gap-0 overflow-hidden p-0">
        <div
          className={cn(
            "flex items-center gap-4 px-6 py-5 text-white",
            danger ? "bg-red-600" : "bg-orange-500"
          )}
        >
          <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-white/20">
            <Icon className="h-9 w-9" strokeWidth={2.25} />
          </span>
          <AlertDialogHeader className="space-y-0.5 text-left">
            <AlertDialogTitle className="text-2xl font-bold">
              {danger ? "Alerta de estoque" : "Aviso de estoque"}
            </AlertDialogTitle>
            <p className="text-sm text-white/90">
              Restam <strong>{percentLabel}</strong> {subject}.
            </p>
          </AlertDialogHeader>
        </div>

        <AlertDialogDescription asChild className="text-foreground">
          <div className="space-y-4 px-6 py-5">
            <dl className="grid grid-cols-2 gap-x-4 gap-y-3 rounded-lg border bg-muted/40 p-4 text-sm">
              <div className="col-span-2">
                <dt className="text-muted-foreground">Concurso</dt>
                <dd className="font-medium">
                  <span style={{ color: current.lottery_color ?? undefined }}>{current.lottery_name}</span>{" "}
                  {current.contest_name} · validade {formatDate(current.validation_date)}
                </dd>
              </div>
              <div className="col-span-2">
                <dt className="text-muted-foreground">Produto</dt>
                <dd className="font-medium">
                  {current.scope === "contest" ? "Estoque final (todos os produtos)" : current.product_name}
                </dd>
              </div>
              <div>
                <dt className="text-muted-foreground">Cotas disponíveis</dt>
                <dd className="font-medium">
                  {current.available_quantity} de {current.created_quantity}
                </dd>
              </div>
              <div>
                <dt className="text-muted-foreground">Faixa</dt>
                <dd className="font-medium">{current.threshold}%</dd>
              </div>
            </dl>
            <StockLevelBar percent={percent} className="w-full" />
          </div>
        </AlertDialogDescription>

        <AlertDialogFooter className="items-center gap-2 border-t px-6 py-4 sm:justify-between">
          <span className="text-sm text-muted-foreground">
            {alerts.length > 1 ? `Mais ${alerts.length - 1} alerta(s) na fila` : ""}
          </span>
          <div className="flex gap-2">
            <Button variant="outline" disabled={acknowledging} onClick={() => acknowledge(true)}>
              Ciente e abrir estoque
            </Button>
            <AlertDialogAction
              onClick={() => acknowledge()}
              disabled={acknowledging}
              className={cn("text-white", danger ? "bg-red-600 hover:bg-red-700" : "bg-orange-500 hover:bg-orange-600")}
            >
              Ciente
            </AlertDialogAction>
          </div>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
